
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useIdleTimer } from "react-idle-timer";
import UserInactiveModal from "../modals/UserInactiveModal";

const LAST_ACTIVITY_KEY = "lastActivityTimeMs";
const FORCE_LOGOUT_KEY = "forceLogout";
const STAY_SIGNED_IN_KEY = "staySignedIn";

const NewIdletimerComponent = ({ handleSignout }) => {
  const userPreferenceIdleTime = useSelector(
    (state) => state.userPrefrenceandReducer
  );
  const useIdleTime = userPreferenceIdleTime?.userPreference?.find(
    (pref) => pref.attributetypecd === "IdleTime"
  )?.attributevalue;

  let idleMinutes = Number(useIdleTime);
  idleMinutes = isNaN(idleMinutes) || idleMinutes < 5 ? 30 : idleMinutes;

  const idleTimeoutMs = useMemo(() => idleMinutes * 60 * 1000, [idleMinutes]);
  const promptTimeoutMs = 60 * 1000;

  const [showWarning, setShowWarning] = useState(false);
  const isPromptedRef = useRef(false);

  // NEW: BroadcastChannel for fast, reliable cross-tab pings
  const bcRef = useRef(null);
  useEffect(() => {
    if ("BroadcastChannel" in window) {
      bcRef.current = new BroadcastChannel("idle-sync");
      bcRef.current.onmessage = (e) => {
        const msg = e.data;
        if (msg === "reset") {
          reset();
        } else if (msg === "stay") {
          isPromptedRef.current = false;
          setShowWarning(false);
          reset();
          activate();
          stampActivity();
        } else if (msg === "logout") {
          handleSignout();
        }
      };
    }
    return () => bcRef.current?.close();
  }, [handleSignout]); // eslint-disable-line

  const post = (msg) => bcRef.current?.postMessage(msg);

  const stampActivity = () => {
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch {}
    post("reset"); // NEW: also ping via BroadcastChannel
  };

  const broadcastForceLogout = () => {
    try { localStorage.setItem(FORCE_LOGOUT_KEY, String(Date.now())); } catch {}
    post("logout");
  };

  const broadcastStaySignedIn = () => {
    try { localStorage.setItem(STAY_SIGNED_IN_KEY, String(Date.now())); } catch {}
    post("stay");
  };

  const onPrompt = () => {
    isPromptedRef.current = true;
    setShowWarning(true);
  };

  const onIdle = () => {
    isPromptedRef.current = false;
    setShowWarning(false);
    broadcastForceLogout();
    handleSignout();
  };

  const onActive = () => {
    isPromptedRef.current = false;
    setShowWarning(false);
    stampActivity();
  };

  const onAction = () => {
    // Any user action in ANY tab should reset all tabs
    stampActivity();
  };

  // Tip: If you suspect followers aren’t getting resets,
  // try leaderElection: false so all tabs broadcast equally.
  const { reset, activate } = useIdleTimer({
    timeout: idleTimeoutMs,
    promptBeforeIdle: promptTimeoutMs,
    onPrompt,
    onIdle,
    onActive,
    onAction,
    crossTab: true,
    leaderElection: true, // set to false if you still see other tabs timing out
    eventsThrottle: 300,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "scroll",
      "visibilitychange"
    ]
  });

  useEffect(() => {
    reset();
  }, [idleTimeoutMs, reset]);

  useEffect(() => {
    // seed last-activity on mount
    stampActivity();
  }, []);

  // Hard wake/focus check (covers sleep/lid close)
  useEffect(() => {
    const hardCheck = () => {
      const now = Date.now();
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || now;
      const elapsed = now - last;

      if (elapsed >= idleTimeoutMs) {
        isPromptedRef.current = false;
        setShowWarning(false);
        broadcastForceLogout();
        handleSignout();
      } else if (elapsed >= idleTimeoutMs - promptTimeoutMs) {
        isPromptedRef.current = true;
        setShowWarning(true);
      }
    };
    const onFocusOrVisible = () => hardCheck();
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    return () => {
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
  }, [idleTimeoutMs, handleSignout]); // eslint-disable-line

  // localStorage listeners (fallback + compatibility)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === FORCE_LOGOUT_KEY) handleSignout();
      if (e.key === STAY_SIGNED_IN_KEY) {
        isPromptedRef.current = false;
        setShowWarning(false);
        reset();
        activate();
        stampActivity();
      }
      if (e.key === LAST_ACTIVITY_KEY) reset();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [handleSignout, reset, activate]); // eslint-disable-line

  const handleStaySignedIn = () => {
    isPromptedRef.current = false;
    setShowWarning(false);
    broadcastStaySignedIn();
    reset();
    activate();
    stampActivity();
  };

  return (
    <UserInactiveModal
      open={showWarning}
      setOpen={setShowWarning}
      logout={() => {
        isPromptedRef.current = false;
        setShowWarning(false);
        broadcastForceLogout();
        handleSignout();
      }}
      logedIn={handleStaySignedIn}
    />
  );
};

export default NewIdletimerComponent;

